import React from 'react';
import { motion } from 'framer-motion';
import { User, Building2, Calendar, Clock, Briefcase } from 'lucide-react';

interface RdvItem {
  id: string;
  prospect_name: string;
  company_name?: string;
  job_title?: string;
  date: string;
  time: string;
  client_name: string;
  sdr_name: string;
  created_at: string;
}

interface RdvListGridProps {
  rdvs: RdvItem[];
  onRdvClick: (rdv: RdvItem) => void;
}

const RdvListGrid: React.FC<RdvListGridProps> = ({ rdvs, onRdvClick }) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-teal-500'
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const formatTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Tous les RDVs pris</h3>
          <p className="text-sm text-gray-500 mt-1">
            {rdvs.length} rendez-vous • Cliquez pour voir les détails
          </p>
        </div>
      </div>

      {rdvs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Calendar className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-base font-medium">Aucun RDV pris aujourd'hui</p>
          <p className="text-sm mt-2">Les nouveaux RDVs apparaîtront ici</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[calc(100vh-550px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {rdvs.map((rdv, index) => (
            <motion.div
              key={rdv.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.02 }}
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onRdvClick(rdv)}
              className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-xl p-4 cursor-pointer hover:border-cyan-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div
                    className={`${getAvatarColor(
                      rdv.sdr_name
                    )} h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm group-hover:shadow-md transition-shadow`}
                  >
                    {getInitials(rdv.sdr_name)}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">{rdv.sdr_name}</p>
                    <div className="flex items-center space-x-1 mt-0.5">
                      <Clock className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-600">{formatTime(rdv.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded-lg text-xs font-medium border border-blue-100">
                  {rdv.client_name}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {rdv.prospect_name || 'N/A'}
                  </p>
                </div>

                {rdv.company_name && (
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <p className="text-xs text-gray-600 truncate">{rdv.company_name}</p>
                  </div>
                )}

                {rdv.job_title && (
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <p className="text-xs text-gray-600 truncate">{rdv.job_title}</p>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-3.5 w-3.5 text-cyan-600" />
                  <span className="text-xs font-medium text-gray-700">
                    {new Date(rdv.date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short'
                    })}
                  </span>
                </div>
                <div className="bg-cyan-50 text-cyan-700 px-2 py-1 rounded text-xs font-semibold">
                  {rdv.time}
                </div>
              </div>

              <div className="mt-3 text-center">
                <span className="text-xs text-cyan-600 font-medium group-hover:text-cyan-700 transition-colors">
                  Cliquer pour plus de détails →
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RdvListGrid;
